import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import slugify from 'slugify';
import styled, { css } from 'styled-components';

import ChannelsFilter from '@/components/ChannelsFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilterWithPresets from '@/components/DateRangeFilterWithPresets';
import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import TopicsFilter from '@/components/TopicsFilter';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import useDebounce from '@/hooks/useDebounce';
import useDidUpdate from '@/hooks/useDidUpdate';
import { selectTeamSettings } from '@/selectors';
import {
  PaginatedResponse,
  SortState,
  TopVideosResource,
  VideoTopicResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';

import TopVideosFilter from './TopVideosFilter';
import TopVideosTableRow from './TopVideosTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'views',
};

const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
  flex: 1;
`;

const LargeTh = styled(Th)`
  min-width: 120px;
`;

const FiltersHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledTable = styled(Table)<{ $areThumbsEnlarged: boolean }>`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 128px;
      min-width: 128px;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
      ${({ $areThumbsEnlarged }) =>
        $areThumbsEnlarged &&
        css`
          width: 312px;
          min-width: 312px;
        `}
    }
    &:nth-child(2) {
      min-width: 200px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 128px;
      top: 0;
      bottom: 0;
      ${({ $areThumbsEnlarged }) =>
        $areThumbsEnlarged &&
        css`
          left: 312px;
        `}
    }
    &:last-child {
      min-width: 56px;
      position: relative;
    }
  }
`;

const FilterHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  gap: 16px;
`;

const LiveVideosControls = styled(Controls)`
  flex-shrink: 0;
  margin-top: 8px;
  border: 1px solid rgba(196, 196, 196, 0.2);
  border-radius: 4px;
  padding: 4px;
`;

const StyledChannelsFilter = styled(ChannelsFilter)`
  margin-bottom: 12px;
`;

const columns = [
  {
    title: 'Thumbnail & Title',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Published',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'AI Comment Sum (Positive)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'AI Comment Sum (Negative)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'AI Comment Sum (New Ideas)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Views',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Duration',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'V7',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'V30',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'VPH7',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'VPH30',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'V7 Index',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'V30 Index',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Revenue',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'RPM',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'R7',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'R30',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'R7 Index',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'R30 Index',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Testing Date',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 30s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 45s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 60s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 80s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 120s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 150s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 30m (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 60m (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e8min (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 4min (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e60min (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e30min (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e4min (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e150s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e120s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e80s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e60s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e45s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e30s (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'ViewRate (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Views (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '25% View Rate (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '50% View Rate (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '75% View Rate (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '100% View Rate (Watch)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Rec7',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Rec30',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'ARP7',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'ARP30',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Script topics',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Thumb topics',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Title topics',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Video type',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '(T) File Date',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) CTR Percentage',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) P25',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) P50',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) P75',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) P100',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) Views',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) eYTV 1m',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) eYTV 2m',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) eYTV 4m',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) eYTV 8m',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) eYTV 30m',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) e1min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) e2min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) e4min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) e8min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '(T) e30min',
    toggable: true,
    defaultVisibility: false,
  },
];

const optionsDataKey = 'videosTable';

const filterConditions = [
  { value: 'OR', title: 'Or' },
  { value: 'AND', title: 'And' },
];

enum Tabs {
  Competitors = 1,
  Owned = 0,
  All = -1,
}

interface Props {
  showTopicsFilter?: boolean;
}

const TopVideosTable: FC<Props> = ({ showTopicsFilter = true }) => {
  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsDataKey],
  );

  const organisationName = useSelector(
    (rootState: RootState) => rootState.auth.user!.organisation!.name,
  );

  const now = DateTime.now();

  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({
    rangeStart: now.minus({ days: 7 }),
    rangeEnd: now,
  });

  const [topics, setTopics] = useState<VideoTopicResource[]>([]);

  const [activeTab, setActiveTab] = useState(Tabs.Competitors);

  const [showLiveVideos, setShowLiveVideos] = useState(false);

  const [areThumbsEnlarged, setAreThumbsEnlarged] = useState(false);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 350);

  const renderTabHeading = (tab: Tabs) => {
    switch (tab) {
      case Tabs.Competitors:
        return 'Competitors';
      case Tabs.Owned:
        return organisationName;
      default:
        return 'All';
    }
  };

  const [activeFilterCondition, setActiveFilterCondition] = useState(
    filterConditions[0],
  );

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!),
  );

  const {
    top_videos_min_views,
    top_videos_min_duration,
    top_videos_min_v7_index,
    top_videos_max_duration,
  } = teamSettings ?? {};

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const params = {
    ...(!showLiveVideos && {
      'filter[was_live]': 0,
    }),
    'filter[unreachable]': 0,
    ...(activeTab !== Tabs.All && {
      'filter[competitor]': activeTab,
    }),
    ...(filteredChannelsIds?.length && {
      'filter[channel_id]': filteredChannelsIds.join(','),
    }),
    ...(!!topics.length && {
      'filter[topic]': topics.map((topic) => topic.id).join(','),
      topic_operator: activeFilterCondition.value,
    }),
    ...(!!top_videos_min_duration && {
      'filter[min_duration]': top_videos_min_duration,
    }),
    ...(!!top_videos_max_duration && {
      'filter[max_duration]': top_videos_max_duration,
    }),
    ...(!!top_videos_min_views && {
      'filter[views]': top_videos_min_views,
    }),
    ...(!!top_videos_min_v7_index && {
      'filter[v7_index]': top_videos_min_v7_index,
    }),
    ...(selectedDateRange.rangeStart && {
      'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
    }),
    ...(selectedDateRange.rangeEnd && {
      'filter[dateTo]': selectedDateRange.rangeEnd.plus({ days: 1 }).toString(),
    }),
    ...(sort.direction !== 'DEFAULT' && {
      sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
    }),
    ...(!!debouncedSearch && { 'filter[search]': debouncedSearch }),
  };

  const fetchVideos = ({ pageParam = `/v1/teams/${teamId}/videos/top` }) => {
    return api.get<never, PaginatedResponse<TopVideosResource>>(pageParam, {
      params,
    });
  };

  const videosQueryKey = ['top-videos', { teamId }];

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    remove,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<TopVideosResource>>(
    videosQueryKey,
    fetchVideos,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [
    teamSettings,
    filteredChannelsKey,
    activeTab,
    sort,
    topics,
    selectedDateRange,
    activeFilterCondition.value,
    debouncedSearch,
    showLiveVideos,
  ]);

  const videos = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<TopVideosResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  const fetchCsv = () =>
    api.get<never, string>(`/v1/teams/${teamId}/top-videos/export`, {
      params,
    });

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.Owned}
            onClick={() => setActiveTab(Tabs.Owned)}
          >
            {renderTabHeading(Tabs.Owned)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Competitors}
            onClick={() => setActiveTab(Tabs.Competitors)}
          >
            {renderTabHeading(Tabs.Competitors)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.All}
            onClick={() => setActiveTab(Tabs.All)}
          >
            {renderTabHeading(Tabs.All)}
          </ControlsButton>
        </Controls>
        <FiltersHolder>
          <ExportButton
            fetchCsv={fetchCsv}
            fileName={`top-videos-${
              !!selectedDateRange.rangeStart
                ? slugify(selectedDateRange.rangeStart.toFormat('yyyy_LL_dd')) +
                  '-'
                : ''
            }${slugify(now.toFormat('yyyy_LL_dd'))}.csv`}
          />
          {showTopicsFilter && (
            <TopicsFilter
              setActiveCondition={setActiveFilterCondition}
              activeCondition={activeFilterCondition}
              conditions={filterConditions}
              topics={topics}
              setTopics={setTopics}
            />
          )}
          <ViewOptionsModal columns={columns} tableKey={optionsDataKey} />
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <StyledChannelsFilter />
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by name"
      />
      {teamId !== 'all' && (
        <FilterHolder>
          <TopVideosFilter />
          <div>
            <Text $type="smallHeadline">Live Videos</Text>
            <LiveVideosControls>
              <ControlsButton
                active={!showLiveVideos}
                onClick={() => setShowLiveVideos(false)}
                gray
              >
                Hide
              </ControlsButton>
              <ControlsButton
                active={showLiveVideos}
                onClick={() => setShowLiveVideos(true)}
              >
                Show
              </ControlsButton>
            </LiveVideosControls>
          </div>
        </FilterHolder>
      )}
      <TableHeader>
        <StyledSectionHeading>
          {renderTabHeading(activeTab)} videos
        </StyledSectionHeading>
        <DateRangeFilterWithPresets
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
        />
      </TableHeader>
      <ScrollableTable>
        <StyledTable $areThumbsEnlarged={areThumbsEnlarged}>
          <thead>
            <tr>
              <Th>Thumbnail</Th>
              <Th>
                Video title
                <br />
                <Text as="span" $color="gray" $type="extraSmall">
                  Channel name
                </Text>
              </Th>
              {tableViewOptions['AI Comment Sum (Positive)'] && (
                <SortableTh
                  property="positive_comments_summary"
                  setSort={setSort}
                  sort={sort}
                  title="AI Comment Sum (Positive)"
                >
                  <InfoTooltip
                    content="AI-generated summary of positive comments and expressions in particular video"
                    title="AI Comment Sum (Positive)"
                  />
                </SortableTh>
              )}
              {tableViewOptions['AI Comment Sum (Negative)'] && (
                <SortableTh
                  property="negative_comments_summary"
                  setSort={setSort}
                  sort={sort}
                  title="AI Comment Sum (Negative)"
                >
                  <InfoTooltip
                    content="AI-generated summary of negative comments and expressions in particular video"
                    title="AI Comment Sum (Negative)"
                  />
                </SortableTh>
              )}
              {tableViewOptions['AI Comment Sum (New Ideas)'] && (
                <SortableTh
                  property="new_ideas_summary"
                  setSort={setSort}
                  sort={sort}
                  title="AI Comment Sum (New Ideas)"
                >
                  <InfoTooltip
                    content="AI-generated summary of given ideas in comments of particular video"
                    title="AI Comment Sum (New Ideas)"
                  />
                </SortableTh>
              )}
              {tableViewOptions.Published && (
                <SortableTh
                  property="publishedAt"
                  title="Published"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Views'] && (
                <SortableTh
                  property="views"
                  title="Total Views"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Duration'] && (
                <SortableTh
                  property="duration"
                  title="Duration"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.V7 && (
                <SortableTh
                  property="v7"
                  title="V7"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.V30 && (
                <SortableTh
                  property="v30"
                  title="V30"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.VPH7 && (
                <SortableTh
                  property="vph7"
                  title="VPH7"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 7 days period among videos with selected topic"
                    title="VPH7"
                  />
                </SortableTh>
              )}
              {tableViewOptions.VPH30 && (
                <SortableTh
                  property="vph30"
                  title="VPH30"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 30 days period among videos with selected topic"
                    title="VPH30"
                  />
                </SortableTh>
              )}
              {tableViewOptions['V7 Index'] && (
                <SortableTh
                  property="v7Index"
                  title="V7 Index"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average V7 among videos with selected topic compared to channel's average V7 of the last 90 days"
                    title="V7 Index"
                  />
                </SortableTh>
              )}
              {tableViewOptions['V30 Index'] && (
                <SortableTh
                  property="v30Index"
                  title="V30 Index"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average V30 among videos with selected topic compared to channel's average V30 of the last 90 days"
                    title="V30 Index"
                  />
                </SortableTh>
              )}
              {tableViewOptions.Revenue && (
                <SortableTh
                  property="revenue"
                  title="Revenue"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.RPM && (
                <SortableTh
                  property="rpm"
                  title="RPM"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average Revenue per 1000 views among videos with selected topic"
                    title="Rpm"
                  />
                </SortableTh>
              )}
              {tableViewOptions.R7 && (
                <SortableTh
                  property="r7"
                  title="R7"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.R30 && (
                <SortableTh
                  property="r30"
                  title="R30"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['R7 Index'] && (
                <SortableTh
                  property="r7Index"
                  title="R7 Index"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average R7 among videos with selected topic compared to channel's average R7 of the last 90 days"
                    title="R7 Index"
                  />
                </SortableTh>
              )}
              {tableViewOptions['R30 Index'] && (
                <SortableTh
                  property="r30Index"
                  title="R30 Index"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average R30 among videos with selected topic compared to channel's average R30 of the last 90 days"
                    title="R30 Index"
                  />
                </SortableTh>
              )}
              {tableViewOptions['Testing Date'] && (
                <SortableTh
                  property="ga_watch_updated_at"
                  title="Testing Date"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Testing Date" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv"
                  title="eYTV (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 30s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_30s"
                  title="eYTV 30s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 30s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 45s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_45s"
                  title="eYTV 45s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 45s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 60s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_60s"
                  title="eYTV 60s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 60s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 80s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_80s"
                  title="eYTV 80s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 80s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 120s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_120s"
                  title="eYTV 120s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 120s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 150s (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_150s"
                  title="eYTV 150s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 150s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 30m (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_30m"
                  title="eYTV 30m (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 30m (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 60m (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_60m"
                  title="eYTV 60m (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 60m (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e8min (Watch)'] && (
                <SortableTh
                  property="ga_watch_views_percentage"
                  title="e8min (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e8min (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 4min (Watch)'] && (
                <SortableTh
                  property="ga_watch_eytv_4"
                  title="eYTV 4min (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 4min (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e60min (Watch)'] && (
                <SortableTh
                  property="ga_watch_e60m"
                  title="e60min (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e60min (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e30min (Watch)'] && (
                <SortableTh
                  property="ga_watch_e30m"
                  title="e30min (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e30min (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e4min (Watch)'] && (
                <SortableTh
                  property="ga_watch_e4min"
                  title="e4min (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e4min (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e150s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e150s"
                  title="e150s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e150s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e120s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e120s"
                  title="e120s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e120s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e80s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e80s"
                  title="e80s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e80s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e60s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e60s"
                  title="e60s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e60s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e45s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e45s"
                  title="e45s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e45s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['e30s (Watch)'] && (
                <SortableTh
                  property="ga_watch_e30s"
                  title="e30s (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e30s (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['ViewRate (Watch)'] && (
                <SortableTh
                  property="ga_watch_view_rate_percentage"
                  title="ViewRate (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="ViewRate (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['Views (Watch)'] && (
                <SortableTh
                  property="ga_watch_views"
                  title="Views (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Views (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['25% View Rate (Watch)'] && (
                <SortableTh
                  property="ga_watch_p25_rate"
                  title="25% View Rate (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="25% View Rate (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['50% View Rate (Watch)'] && (
                <SortableTh
                  property="ga_watch_p50_rate"
                  title="50% View Rate (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="50% View Rate (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['75% View Rate (Watch)'] && (
                <SortableTh
                  property="ga_watch_p75_rate"
                  title="75% View Rate (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="75% View Rate (Watch)" />
                </SortableTh>
              )}
              {tableViewOptions['100% View Rate (Watch)'] && (
                <SortableTh
                  property="ga_watch_p100_rate"
                  title="100% View Rate (Watch)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="100% View Rate (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['Rec7'] && (
                <SortableTh
                  property="yt_rec_7"
                  title="Rec7"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Rec7" />
                </SortableTh>
              )}
              {tableViewOptions['Rec30'] && (
                <SortableTh
                  property="yt_rec_30"
                  title="Rec30"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Rec30" />
                </SortableTh>
              )}
              {tableViewOptions['ARP7'] && (
                <SortableTh
                  property="yt_avg_position_7"
                  title="ARP7"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="ARP7" />
                </SortableTh>
              )}
              {tableViewOptions['ARP30'] && (
                <SortableTh
                  property="yt_avg_position_30"
                  title="ARP30"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="ARP30" />
                </SortableTh>
              )}
              {tableViewOptions['Script topics'] && (
                <LargeTh>
                  Script Topics
                  <InfoTooltip
                    content="The main topic of the video, should be added after watching the video"
                    title="Script Topics"
                  />
                </LargeTh>
              )}
              {tableViewOptions['Thumb topics'] && (
                <LargeTh>
                  Thumb Topics
                  <InfoTooltip
                    content="Prominent elements in the thumbnail, that create the first impression about the video"
                    title="Thumb Topics"
                  />
                </LargeTh>
              )}
              {tableViewOptions['Title topics'] && (
                <LargeTh>
                  Title Topics
                  <InfoTooltip
                    content="Automatically assigned tags, added periodically by analyzing title"
                    title="Title Topics"
                  />
                </LargeTh>
              )}
              {tableViewOptions['Video type'] && (
                <LargeTh>
                  Video Type
                  <InfoTooltip
                    content='Flexible field to group videos into categories like "animation", "reuse", etc'
                    title="Video Type"
                  />
                </LargeTh>
              )}
              {tableViewOptions['(T) File Date'] && (
                <SortableTh
                  property="t_file_date"
                  title="(T) File Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) CTR Percentage'] && (
                <SortableTh
                  property="t_view_rate_percentage"
                  title="(T) CTR Percentage"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) P25'] && (
                <SortableTh
                  property="t_p25"
                  title="(T) P25"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) P50'] && (
                <SortableTh
                  property="t_p50"
                  title="(T) P50"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) P75'] && (
                <SortableTh
                  property="t_p75"
                  title="(T) P75"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) P100'] && (
                <SortableTh
                  property="t_p100"
                  title="(T) P100"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) Views'] && (
                <SortableTh
                  property="t_video_views"
                  title="(T) Views"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) eYTV 1m'] && (
                <SortableTh
                  property="t_eytv_1m"
                  title="(T) eYTV 1m"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) eYTV 2m'] && (
                <SortableTh
                  property="t_eytv_2m"
                  title="(T) eYTV 2m"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) eYTV 4m'] && (
                <SortableTh
                  property="t_eytv_4m"
                  title="(T) eYTV 4m"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) eYTV 8m'] && (
                <SortableTh
                  property="t_eytv_8m"
                  title="(T) eYTV 8m"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) eYTV 30m'] && (
                <SortableTh
                  property="t_eytv_30m"
                  title="(T) eYTV 30m"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) e1min'] && (
                <SortableTh
                  property="e1m"
                  title="(T) e1min"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) e2min'] && (
                <SortableTh
                  property="e2m"
                  title="(T) e2min"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) e4min'] && (
                <SortableTh
                  property="e4m"
                  title="(T) e4min"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) e8min'] && (
                <SortableTh
                  property="e8m"
                  title="(T) e8min"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['(T) e30min'] && (
                <SortableTh
                  property="e30m"
                  title="(T) e30min"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!videos.length}
            >
              {videos.map((video) => (
                <TopVideosTableRow
                  key={video.id}
                  video={video}
                  optionsKey={optionsDataKey}
                  videosQueryKey={videosQueryKey}
                  areThumbsEnlarged={areThumbsEnlarged}
                  setAreThumbsEnlarged={setAreThumbsEnlarged}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default TopVideosTable;
